import { useTranslation } from 'react-i18next'
import { List, ListItemButton, ListItemText, ListProps } from '@mui/material'
import { usePolicy } from '../../contexts/PolicyContext'
import { humanizeTimestamp } from '../../utils/date'
import { Asset, AxiomaW1 } from '../../types'
import { useClipboard } from '../../hooks/clipboard'
import AssetNameListItemButton from '../AssetNameListItemButton'
import AssetCompanyListItemButton from '../AssetCompanyListItemButton'
import AssetProjectListItemButton from '../AssetProjectListItemButton'
import AssetLevelListItemButton from '../AssetLevelListItemButton'
import AssetLiftListItemButton from '../AssetLiftListItemButton'
import {
  AssetAttachedAtListItem,
  AssetDetachedAtListItem,
  AssetPhotoListItem
} from '../AssetDetailsList'
import {
  CatalogProductManufacturerListItem,
  CatalogProductManufacturerProductCodeListItem,
  CatalogProductNameListItem
} from '../CatalogProductDetailsList'

type AxiomaW1Field =
  | 'id'
  | 'sim_id'
  | 'nominal_diameter'
  | 'created_at'
  | 'asset.name'
  | 'asset.company.name'
  | 'asset.project.name'
  | 'asset.level.name'
  | 'asset.lift.name'
  | 'asset.attachedAt'
  | 'asset.detachedAt'
  | 'catalog_product.name'
  | 'catalog_product.manufacturer'
  | 'catalog_product.manufacturerProductCode'

export interface AxiomaW1DetailsProps extends Omit<ListProps, 'children'> {
  w1: AxiomaW1
  fields?: AxiomaW1Field[]
  editable?: boolean
  onUpdate?: (w1: AxiomaW1) => void
}

export default function AxiomaW1Details({
  w1,
  editable = false,
  fields = [
    'id',
    'sim_id',
    'nominal_diameter',
    'created_at',
    'asset.name',
    'asset.company.name',
    'asset.project.name',
    'asset.level.name',
    'asset.lift.name'
  ],
  onUpdate,
  ...props
}: AxiomaW1DetailsProps) {
  const { t } = useTranslation('axioma')

  const { copyToClipboard } = useClipboard()

  const { isSuperadmin, isManufacturer } = usePolicy()

  const canViewSim = isSuperadmin || isManufacturer

  const handleAssetUpdate = ({ axiomaW1: _, ...asset }: Asset) => {
    onUpdate?.({ ...w1, asset_id: asset.id, asset })
  }

  return (
    <List {...props}>
      {w1?.asset && fields.includes('asset.name') && (
        <AssetNameListItemButton
          key="asset.name"
          asset={w1.asset}
          editable={editable}
          afterUpdate={handleAssetUpdate}
        />
      )}

      {fields.includes('id') && (
        <ListItemButton key="id" onClick={() => copyToClipboard(w1.id)}>
          <ListItemText primary={w1.id} secondary={t('serialNumber')} />
        </ListItemButton>
      )}

      {w1?.asset && fields.includes('asset.company.name') && (
        <AssetCompanyListItemButton
          key="asset.company.name"
          asset={w1.asset}
          editable={editable}
          afterUpdate={handleAssetUpdate}
        />
      )}

      {w1?.asset && fields.includes('asset.project.name') && (
        <AssetProjectListItemButton
          key="asset.project.name"
          asset={w1.asset}
          editable={editable}
          afterUpdate={handleAssetUpdate}
        />
      )}

      {w1?.asset?.project && fields.includes('asset.level.name') && (
        <AssetLevelListItemButton
          key="asset.level.name"
          asset={w1.asset}
          editable={editable}
          afterUpdate={handleAssetUpdate}
        />
      )}

      {w1?.asset?.project && fields.includes('asset.lift.name') && (
        <AssetLiftListItemButton
          key="asset.lift.name"
          asset={w1.asset}
          editable={editable}
          afterUpdate={handleAssetUpdate}
        />
      )}

      {w1?.catalog_product && fields.includes('catalog_product.name') && (
        <CatalogProductNameListItem
          key="catalog_product.name"
          catalogProduct={w1.catalog_product}
        />
      )}

      {w1?.catalog_product && fields.includes('catalog_product.manufacturer') && (
        <CatalogProductManufacturerListItem
          key="catalog_product.manufacturer"
          catalogProduct={w1.catalog_product}
        />
      )}

      {w1?.catalog_product && fields.includes('catalog_product.manufacturerProductCode') && (
        <CatalogProductManufacturerProductCodeListItem
          key="catalog_product.manufacturerProductCode"
          catalogProduct={w1.catalog_product}
        />
      )}

      {fields.includes('nominal_diameter') && (
        <ListItemButton
          key="nominal_diameter"
          onClick={() => copyToClipboard(w1.nominal_diameter.toString())}
        >
          <ListItemText primary={`${w1.nominal_diameter} mm`} secondary={t('nominalDiameter')} />
        </ListItemButton>
      )}

      {w1.sim_id !== null && canViewSim && fields.includes('sim_id') && (
        <ListItemButton key="sim_id" onClick={() => copyToClipboard(w1.sim_id!)}>
          <ListItemText primary={w1.sim_id} secondary={t('simId')} />
        </ListItemButton>
      )}

      {w1?.asset && <AssetPhotoListItem asset={w1.asset} onUpdate={handleAssetUpdate} />}

      {fields.includes('created_at') && (
        <ListItemButton key="created_at">
          <ListItemText primary={humanizeTimestamp(w1.created_at)} secondary={t('createdAt')} />
        </ListItemButton>
      )}

      {w1?.asset?.project && fields.includes('asset.attachedAt') && (
        <AssetAttachedAtListItem
          asset={w1.asset}
          editable={editable}
          afterSubmit={handleAssetUpdate}
        />
      )}

      {w1?.asset?.project && fields.includes('asset.detachedAt') && (
        <AssetDetachedAtListItem
          asset={w1.asset}
          editable={editable}
          afterSubmit={handleAssetUpdate}
        />
      )}
    </List>
  )
}
